import { InvalidUrlError, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../../../../Appy';
import { currentUserStore } from '../../../../CurrentUserStore';
import ErrorActionableReaction, { EAProps, handleError } from '../../../../ErrorActionableReaction';
import { warnAndNotify } from '../../../../Honeybadger';
import { MissingLinkError, findLinkT } from '../../../../LinkyLinky';
import { participantResourceDecoder } from '../../../../ParticipantsStore/Decoders';
import { Link } from '../../../../Resource/Types';
import { bulkGroupMembershipsResourceDecoder } from '../../Participants/BulkActions/AddParticipantsToGroup/UserGroupMembershipsForm/Store/Decoders';
import AddParticipantStore from '.';
import { State } from './Types';

interface Props extends EAProps<AddParticipantStore> {}

type AddParticipantError = AppyError | InvalidUrlError | MissingLinkError;

const handleGroupError = (store: AddParticipantStore) => (error: AddParticipantError) => {
  switch (error.kind) {
    case 'missing-link-error':
    case 'invalid-url-error':
      store.error('We are unable to process your request at this time');
      warnAndNotify('GroupReactions', error.kind, error);
      break;
    default:
      handleError(store, error);
  }
};

const sendRequest = callApi(participantResourceDecoder, {});

const buildUrl = (link: Link): Task<AddParticipantError, Link> =>
  toUrlT(link.href).map((url): Link => ({ ...link, href: url.href }));

const getParticipant = (link: Link) => buildUrl(link).andThen(sendRequest);

const processLink =
  (id: string) =>
  (link: Link): Task<AddParticipantError, Link> =>
    toUrlT(link.href.replace('{participant_id}', `${id}`)).map(
      (url): Link => ({ ...link, href: url.href }),
    );

const bulkGroupMembershipsEncoder = (groupId: number, ids: number[]) => ({
  user_group_memberships: {
    user_ids: ids,
    user_group_id: groupId,
  },
});

class AddParticipantReactions extends ErrorActionableReaction<AddParticipantStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'new-participant':
        Task.succeed<AddParticipantError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('participant'))
          .andThen(processLink(state.participantId))
          .andThen(getParticipant)
          .fork(handleGroupError(store), store.readyWithNewParticipant);
        break;
      case 'remove-participant':
        store.readyWithRemovedParticipant();
        break;
      case 'submitting':
        Task.succeed<AddParticipantError, ReadonlyArray<Link>>(store.links)
          .andThen(findLinkT('update'))
          .andThen(
            callApi(
              bulkGroupMembershipsResourceDecoder,
              bulkGroupMembershipsEncoder(
                state.groupParticipantsResource.payload.id,
                state.newParticipants.map((p) => p.payload.id),
              ),
            ),
          )
          .fork(handleGroupError(store), store.participantsAdded);
        break;
      case 'ready':
      case 'participants-added':
        break;
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default AddParticipantReactions;
